
import Vue from "vue";
import { AuthSignup } from "@/interfaces/auth/auth_signup";
import { MapAutoCompleteResult } from "@/interfaces/onboarding/onboarding";
import {
  cities,
  get_password_validator_regex,
  parseStrToInt
} from "@/utils/global";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { email } from "vuelidate/lib/validators";
import { GET_USER_DETAILS, SIGNUP_USER } from "@/store/modules/auth/constants";
import { SITE_DIRECTION } from "@/store/modules/common/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import SignupHeader from "@/views/auth/SignupHeader.vue";
import { SIGNUP_PERSONA_OPTIONS } from "@/utils/assessment_persona";

const NONE_OF_ABOVE_PERSONA = "none_of_above";

export default Vue.extend({
  name: "InceptionSignupSteps",
  components: { SignupHeader },
  data() {
    return {
      step: 1,
      loading: false,
      valid: false,
      show_job_role_step: false,
      validate_step6: false,
      source_link:
        "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_1.webm",
      text_question: this.$t("candidate-onboarding.form.full-name").toString(),
      payload: {
        valid_form: false,
        signup_form: {
          name: "",
          mobile_number: "",
          eid_number: "",
          al_ain_user: null,
          address: "",
          education: "",
          program: "",
          availability: "true",
          laptop: "true",
          accommodations: "true",
          wifi_availability: "true",
          email: "",
          password: "",
          nationality: null,
          is_employed: "true",
          city: this.$t("auth.al-ain-city").toString(),
          is_student: "true",
          university_name: "",
          persona: null
        },
        show_password: false,
        education: [
          this.$t("auth.high-school").toString(),
          this.$t("auth.bachelors").toString(),
          this.$t("auth.masters").toString(),
          this.$t("auth.phd").toString()
        ],
        program: [
          this.$t("auth.linkedin").toString(),
          this.$t("auth.mawahed").toString(),
          this.$t("auth.instagram").toString(),
          this.$t("auth.word_of_mouth").toString(),
          this.$t("auth.others").toString()
        ],
        persona_options: [
          ...SIGNUP_PERSONA_OPTIONS,
          { title: "None of the above", value: NONE_OF_ABOVE_PERSONA }
        ],
        cities: cities
        // nationality: [this.$t("auth.uae").toString()]
      } as AuthSignup,
      mobileNumberError: ""
    };
  },
  computed: {
    eidValidationRules(): Array<(v: string) => boolean | string> {
      return [
        (v: string) => !!v || this.$t("rules.required").toString(),
        (v: string) => this.validateEidPositive(v),
        (v: string) =>
          (v && v.length === 15) || this.$t("rules.signup.eid").toString(),
        (v: string) => this.validateEidNumber(v)
      ];
    },
    ...mapGetters("common", {
      get_site_direction: SITE_DIRECTION
    }),
    ...mapGetters("auth", {
      get_user_detail: GET_USER_DETAILS
    })
  },
  created() {
    // Check if returning from verify-email with a specific step
    const stepParam = this.$route.query.step;
    if (stepParam && !isNaN(Number(stepParam))) {
      this.step = Number(stepParam);
      if (!this.show_job_role_step && this.step === 4) {
        this.step = 5;
      }
      // Set the appropriate source link and question for the step
      this.text_question = this.$t(this.get_step_value()).toString();
    }

    if (this.get_site_direction === SiteDirections.RTL)
      this.source_link =
        "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_1.webm";
    else
      this.source_link =
        "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_1.webm";
  },
  watch: {
    get_site_direction() {
      // Set the source link based on the site direction
      this.text_question = this.$t(this.get_step_value()).toString();
      // Set the payload values based on the site direction
      const selected_index = this.payload.education.indexOf(
        this.payload.signup_form.education
      );
      // const selected_city = this.payload.cities?.indexOf(
      //   this.payload?.signup_form.city
      // );
      this.payload.education = [
        this.$t("auth.high-school").toString(),
        this.$t("auth.bachelors").toString(),
        this.$t("auth.masters").toString(),
        this.$t("auth.phd").toString()
      ];
      this.payload.program = [
        this.$t("auth.linkedin").toString(),
        this.$t("auth.mawahed").toString(),
        this.$t("auth.word_of_mouth").toString()
      ];
      // this.payload.cities = [
      //   this.$t("auth.al-ain-city").toString(),
      //   this.$t("auth.abu-dhabi").toString(),
      //   this.$t("auth.dubai").toString(),
      //   this.$t("auth.sharjah").toString(),
      //   this.$t("auth.ajman").toString(),
      //   this.$t("auth.fujairah").toString(),
      //   this.$t("auth.ras-al-khaimah").toString(),
      //   this.$t("auth.um-al-quwain").toString()
      // ];

      // this.payload.signup_form.city = this.payload.cities[selected_city];
      this.payload.signup_form.education =
        this.payload.education[selected_index];
      this.payload.signup_form.program = this.payload.program[selected_index];
    }
  },
  methods: {
    email,
    get_password_validator_regex,
    parseStrToInt,
    ...mapActions("auth", {
      signup_user: SIGNUP_USER
    }),
    ...mapMutations({
      set_root_error: ROOT_ERROR,
      set_root_notification: ROOT_NOTIFICATION
    }),
    on_next() {
      let form = `form${this.step + 1}`;
      if (this.$refs?.[form]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.$refs[form] as any).resetValidation(); // Reset validation before revalidating
      }
      // Checking the current Step and validating the form & setting the sourcelink & text based on the step
      if (this.step === 1) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form1 as any)?.validate()) {
          this.step++;
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_2.webm",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_2.webm"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.mobile-number"
          ).toString();
        }
      } else if (this.step === 2) {
        if (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this.$refs?.form2 as any)?.validate() &&
          this.mobileNumberError === ""
        ) {
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_4.webm",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_4.webm"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.eid-number"
          ).toString();
          this.step++;
        }
      } else if (this.step === 3) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form3 as any)?.validate()) {
          if (this.show_job_role_step) {
            this.set_source_by_direction(
              "https://ibhcstagingstorage.blob.core.windows.net/ibhc-textract/open/bot/assessment/gt/job_role.webm",
              "https://ibhcstagingstorage.blob.core.windows.net/ibhc-textract/open/bot/assessment/gt/job_role.webm"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.persona"
            ).toString();
            this.step++;
          } else {
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-eng-employed.mp4",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-ar-employed.mp4"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.is-employed"
            ).toString();
            this.step = 5;
          }
        }
      } else if (this.step === 4) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form4 as any)?.validate()) {
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-eng-employed.mp4",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-ar-employed.mp4"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.is-employed"
          ).toString();
          this.step++;
        }
      } else if (this.step === 5) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form5 as any)?.validate()) {
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-student-01.mp4",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Arabic-student-1.mp4"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.is-student"
          ).toString();
          this.step++;
        }
      } else if (this.step === 6) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form6 as any)?.validate()) {
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_8.webm",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_8.webm"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.email"
          ).toString();
          this.step++;
        }
      } else if (this.step === 7) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form7 as any)?.validate()) {
          this.set_source_by_direction(
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_9.webm",
            "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_9.webm"
          );
          this.text_question = this.$t(
            "candidate-onboarding.form.password"
          ).toString();
          this.step++;
        }
      } else if (this.step === 8) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this.$refs?.form8 as any)?.validate()) {
          this.loading = true;
          // Submit Form to Sign-up the user & auto-login
          this.submit_form();
        }
      }
    },
    async on_back() {
      if (this.step === 1) {
        await this.$router.push("/auth");
        return;
      }
      // Handle Source link & text-message based on the step and language
      if (this.step > 1) {
        switch (this.step) {
          case 8:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_8.webm",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_8.webm"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.email"
            ).toString();
            break;
          case 7:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-student-01.mp4",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Arabic-student-1.mp4"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.is-student"
            ).toString();
            break;
          case 6:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-eng-employed.mp4",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Video-ar-employed.mp4"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.is-employed"
            ).toString();
            break;
          case 5:
            if (this.show_job_role_step) {
              this.set_source_by_direction(
                "https://ibhcstagingstorage.blob.core.windows.net/ibhc-textract/open/bot/assessment/gt/job_role.webm",
                "https://ibhcstagingstorage.blob.core.windows.net/ibhc-textract/open/bot/assessment/gt/job_role.webm"
              );
              this.text_question = this.$t(
                "candidate-onboarding.form.persona"
              ).toString();
            } else {
              this.set_source_by_direction(
                "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_4.webm",
                "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_4.webm"
              );
              this.text_question = this.$t(
                "candidate-onboarding.form.eid-number"
              ).toString();
            }
            break;
          case 4:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_4.webm",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_4.webm"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.eid-number"
            ).toString();
            break;
          case 3:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_2.webm",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_2.webm"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.mobile-number"
            ).toString();
            break;
          case 2:
            this.set_source_by_direction(
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/Question_1.webm",
              "https://api-hcms-textract.s3.eu-west-2.amazonaws.com/open/bot/inception_registration/arabic/Question_1.webm"
            );
            this.text_question = this.$t(
              "candidate-onboarding.form.full-name"
            ).toString();
            break;
        }
        if (!this.show_job_role_step && this.step === 5) {
          this.step = 3;
        } else {
          this.step--;
        }
      }
    },
    get_step_value(): string {
      // base on the steps return locale value text
      switch (this.step) {
        case 1:
          return "candidate-onboarding.form.full-name";
        case 2:
          return "candidate-onboarding.form.mobile-number";
        case 3:
          return "candidate-onboarding.form.eid-number";
        case 4:
          return "candidate-onboarding.form.persona";
        case 5:
          return "candidate-onboarding.form.is-employed";
        case 6:
          return "candidate-onboarding.form.is-student";
        case 7:
          return "candidate-onboarding.form.email";
        case 8:
          return "candidate-onboarding.form.password";
        default:
          return "";
      }
    },
    set_source_by_direction(ltr_source: string, rtl_source: string) {
      // Set the source link based on the site direction
      if (this.get_site_direction === SiteDirections.LTR)
        this.source_link = ltr_source;
      else this.source_link = rtl_source;
    },
    input_place(place: MapAutoCompleteResult) {
      // place input by gmap autocomplete
      this.payload.signup_form.address = place.formatted_address;
    },
    async submit_form() {
      // Submit Form to Sign-up the user & auto-login
      const split_name = this.payload.signup_form.name.split(" ");
      const first_name = split_name[0].trim();
      let last_name: string | null = null;
      if (split_name.length > 1) {
        last_name = split_name.slice(1).join(" ").trim();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj: any = {
        first_name: first_name,
        last_name: last_name,
        mobile_number: this.payload.signup_form.mobile_number,
        eid_number: this.payload.signup_form.eid_number,
        email: this.payload.signup_form.email,
        password: this.payload.signup_form.password,
        is_employed:
          this.payload.signup_form.is_employed === "true" ? true : false,
        is_student:
          this.payload.signup_form.is_student === "true" ? true : false,
        work_type: "QUDRA_TECH",
        persona:
          this.payload.signup_form.persona === NONE_OF_ABOVE_PERSONA
            ? null
            : this.payload.signup_form.persona || null
      };
      const result = await this.signup_user(obj);
      if (result) {
        // For company 82 (G42 POC V4), directly login and redirect to dashboard
        await this.$router.push({
          name: "candidate-dashboard"
        });
      }
      this.loading = false;
    },
    handleEidInput(event: Event) {
      const target = event.target as HTMLInputElement;
      let value = target.value;
      // Remove any negative signs or non-numeric characters except digits
      value = value.replace(/[^0-9]/g, "");
      // Update the model with cleaned value
      this.payload.signup_form.eid_number = value;
      // Update the input field value
      target.value = value;
    },
    validateEidPositive(v: string): boolean | string {
      if (!v) return true;
      // Check if value contains negative sign
      if (v.includes("-") || v.startsWith("-")) {
        return this.$t("rules.signup.eid-positive").toString();
      }
      const parsedValue = parseStrToInt(v);
      if (parsedValue !== null && !isNaN(parsedValue) && parsedValue < 0) {
        return this.$t("rules.signup.eid-positive").toString();
      }
      return true;
    },
    validateEidNumber(v: string): boolean | string {
      if (!v) return true;
      const parsedValue = parseStrToInt(v);
      if (parsedValue === null || isNaN(parsedValue)) {
        return this.$t("rules.signup.eid").toString();
      }
      return true;
    },
    checkNumber() {
      let mobile_number = this.payload.signup_form.mobile_number;
      let number = Number(mobile_number);

      if (!Number.isFinite(number) || number <= 0) {
        console.log("error");
        this.mobileNumberError = this.$t("rules.mobile_number").toString(); // ✅ Convert to string
      } else {
        this.mobileNumberError = ""; // Clear error if valid
      }
    }
  }
});
